import React, { useEffect, useRef, useState } from "react";
import { Box, Flex, Grid, Text } from "@chakra-ui/react";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  PATH_PICK_OVERSEAS_RELEASE_ADDRESS,
  PATH_PICK_OVERSEAS_RELEASE_ADDRESS_ROWS,
} from "@/features/pathpick/features/basket/constants";

// 복사 상태를 표시할 대상 키 — 칸별 복사는 주소 필드 키를, 전체 주소 복사는 "all"을 쓴다.
const FULL_ADDRESS_KEY = "all";

// 복사 완료 표시를 유지하는 시간 (환불 계좌 툴팁의 복사 버튼과 동일)
const COPIED_DURATION = 2000;

// 복사 버튼 — 환불 계좌 안내 툴팁(BankAccountInfoTooltipContent)과 같은 모양·피드백을 쓴다.
// 평소엔 회색, hover 시 파랑이고 복사 직후에는 check 아이콘 + 완료 문구로 잠시 바뀐다.
function CopyButton({
  label,
  isCopied,
  onClick,
}: {
  label: string;
  isCopied: boolean;
  onClick: () => void;
}) {
  const { t } = useI18nHelper();

  return (
    <Flex
      gap={"2px"}
      alignItems={"center"}
      flexShrink={0}
      cursor={"pointer"}
      className={"group"}
      onClick={onClick}
    >
      {/* CustomIcon은 색을 상속하므로 hover 색은 감싸는 Box가 들고 있는다. */}
      <Box color={"#8F8F8C"} _groupHover={{ color: "#1272EF" }}>
        <CustomIcon src={isCopied ? "check" : "copy"} width={12} height={12} />
      </Box>
      <Text
        textStyle={"caption-bold"}
        color={"#444444"}
        whiteSpace={"nowrap"}
        _groupHover={{ color: isCopied ? "#444444" : "#1272EF" }}
      >
        {isCopied ? t("path_pick:basket.overseas_release.copy_done") : label}
      </Text>
    </Flex>
  );
}

// 바이어가 네이버 주소록에 그대로 옮겨 적어야 하는 패스픽 해외 출고지 주소.
// 기본 정보 설정 모달의 해외 출고지 섹션과 등록 방법 안내 모달(3단계) 두 곳에서 함께 쓴다.
//
// 값이 줄바꿈되면 주소를 잘못 옮겨 적을 수 있어 모든 칸에 nowrap을 건다 —
// 이 카드를 넣는 쪽이 충분한 폭(720px 기준)을 확보해야 한다.
function PathPickBasketOverseasAddressCard() {
  const { t } = useI18nHelper();
  // 복사 버튼이 7개라 boolean을 각각 두는 대신 "방금 복사한 대상" 하나로 관리한다.
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(resetTimerRef.current);
  }, []);

  const fullAddress = PATH_PICK_OVERSEAS_RELEASE_ADDRESS_ROWS.map(
    ({ key }) => PATH_PICK_OVERSEAS_RELEASE_ADDRESS[key]
  ).join(", ");

  // clipboard API는 비보안 컨텍스트/권한 거부 시 throw한다 — 실패해도 화면은 그대로 두고 로그만 남긴다.
  const handleCopy = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      // 다른 칸을 연달아 복사하면 이전 타이머가 새 표시를 일찍 지우므로 매번 갈아 끼운다.
      clearTimeout(resetTimerRef.current);
      setCopiedKey(key);
      resetTimerRef.current = setTimeout(
        () => setCopiedKey(null),
        COPIED_DURATION
      );
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <Flex
      flexDirection={"column"}
      gap={"10px"}
      padding={"12px"}
      borderRadius={"8px"}
      border={"0.5px solid #D4D4D1"}
      background={"#FFFFFF"}
    >
      <Flex justifyContent={"space-between"} alignItems={"center"} gap={"8px"}>
        <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
          {PATH_PICK_OVERSEAS_RELEASE_ADDRESS.name}
        </Text>
        <CopyButton
          label={t("path_pick:basket.overseas_release.copy_all")}
          isCopied={copiedKey === FULL_ADDRESS_KEY}
          onClick={() => handleCopy(FULL_ADDRESS_KEY, fullAddress)}
        />
      </Flex>

      {/* 6칸을 2컬럼 × 3행으로 — 상수 배열 순서대로 행 우선으로 채워진다.
          값이 줄바꿈되지 않아 좁은 폭에서는 2열이면 넘치므로 한 열로 쌓는다. */}
      <Grid
        templateColumns={{ base: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
        columnGap={"24px"}
        rowGap={"8px"}
      >
        {PATH_PICK_OVERSEAS_RELEASE_ADDRESS_ROWS.map(({ key, labelKey }) => (
          <Flex key={key} alignItems={"center"} gap={"6px"}>
            <Text
              textStyle={"body-sm-medium"}
              color={"#8F8F8C"}
              whiteSpace={"nowrap"}
            >
              {`${t(labelKey)} :`}
            </Text>
            <Text
              textStyle={"body-sm-bold"}
              color={"#444440"}
              whiteSpace={"nowrap"}
            >
              {PATH_PICK_OVERSEAS_RELEASE_ADDRESS[key]}
            </Text>
            <Flex flex={1} />
            <CopyButton
              label={t("path_pick:basket.overseas_release.copy")}
              isCopied={copiedKey === key}
              onClick={() =>
                handleCopy(key, PATH_PICK_OVERSEAS_RELEASE_ADDRESS[key])
              }
            />
          </Flex>
        ))}
      </Grid>
    </Flex>
  );
}

export default PathPickBasketOverseasAddressCard;
